/**
 * The staff register and project crew in Teslyn Cloud (decision 422; docs/specs/cloud/staff-register.md §6). RLS
 * decides: every staff member reads, tenant admins, managers and office staff write, clients never see either. The
 * Operations Hub reads the crew of its project cards with `loadCrewNamed` (the `project_crew_named` view).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { REGISTER_ROLES, REGISTER_ROLE_LABEL, crewOnDate, legacyPersonRole } from '@teslyn/domain';
import type { NamedCrew, Person, ProjectCrew, RegisterRole, StaffImportChange } from '@teslyn/domain';
import type { HubCrewLoader, HubCrewMember } from '../ops/hubApi.ts';

const PERSON_COLUMNS =
  'id, tenant_id, name, role, rank, roles, title, active, email, phone, user_id, created_at, updated_at, updated_by, deleted_at';

const CREW_COLUMNS =
  'id, tenant_id, project_id, person_id, role, start_date, end_date, note, created_at, updated_at, updated_by, deleted_at, person_name';

/** A sign-in of the organisation a register person can be linked to. */
export interface SignInChoice {
  readonly userId: string;
  readonly label: string;
}

function fail(error: { readonly message: string; readonly code?: string } | null, what: string): void {
  if (!error) return;
  if (error.code === '23505' && /person_tenant_name_key/.test(error.message)) {
    throw new Error('That name is already in the staff register.');
  }
  if (error.code === '23505' && /person_user_idx/.test(error.message)) {
    throw new Error('That sign-in is already linked to another person in the register.');
  }
  throw new Error(`${what}: ${error.message}`);
}

/**
 * The organisation's register by name: the live people, or (`all`) the people a tenant admin deleted too, for the
 * Staff page's "Deleted people" and for crew lists that still name them.
 */
export async function loadStaff(
  sb: SupabaseClient,
  tenantId: string,
  which: 'live' | 'all' = 'live',
): Promise<Person[]> {
  const q = sb.from('person').select(PERSON_COLUMNS).eq('tenant_id', tenantId);
  const r = await (which === 'live' ? q.is('deleted_at', null) : q).order('name');
  fail(r.error, 'The staff register could not be read');
  return (r.data ?? []) as Person[];
}

/**
 * Delete a person from the register (tenant admins; MB, 25 Oct 2026): `person_delete` sets `deleted_at`, so records
 * that name them keep the name, and audits it (supabase/migrations/20261025001300_staff_delete.sql).
 */
export async function deleteStaff(sb: SupabaseClient, personId: string): Promise<string> {
  const r = await sb.rpc('person_delete', { p_person: personId });
  fail(r.error, 'Not deleted');
  return (r.data as { deleted_at?: string } | null)?.deleted_at ?? new Date().toISOString();
}

/** Bring a deleted person back (tenant admins); refused, in words, when their name or sign-in was given to someone. */
export async function restoreStaff(sb: SupabaseClient, personId: string): Promise<void> {
  const r = await sb.rpc('person_restore', { p_person: personId });
  if (r.error) throw new Error(`Not restored. ${r.error.message}`);
}

/** Add (`isNew`) or change a register person. The database keeps `role` in step with `roles`. */
/**
 * N11 (decision 471): the register people field crew added on site and the office has not checked yet. Read apart
 * from the register's own columns, so the Staff page works before the migration adds `added_on_site` (empty then).
 */
export async function loadAddedOnSite(sb: SupabaseClient, tenantId: string): Promise<ReadonlySet<string>> {
  const r = await sb
    .from('person')
    .select('id, added_on_site')
    .eq('tenant_id', tenantId)
    .eq('added_on_site', true);
  if (r.error) return new Set();
  return new Set(
    ((r.data ?? []) as { id: string; added_on_site?: boolean }[])
      .filter((x) => x.added_on_site === true)
      .map((x) => x.id),
  );
}

/** N11: "Checked": the office has checked a person added on site; the mark goes. */
export async function checkAddedOnSite(sb: SupabaseClient, personId: string): Promise<void> {
  const r = await sb.from('person').update({ added_on_site: false }).eq('id', personId).select('id');
  fail(r.error, 'Not saved');
  if ((r.data ?? []).length === 0) throw new Error('Not saved: you may not change the staff register.');
}

export async function saveStaff(sb: SupabaseClient, person: Person, isNew: boolean): Promise<Person> {
  const row = {
    tenant_id: person.tenant_id,
    name: person.name,
    role: person.role,
    rank: person.rank,
    roles: person.roles ?? [person.role],
    title: person.title ?? '',
    active: person.active !== false,
    email: person.email ?? null,
    phone: person.phone ?? null,
    user_id: person.user_id ?? null,
  };
  const r = isNew
    ? await sb.from('person').insert(row).select(PERSON_COLUMNS)
    : await sb.from('person').update(row).eq('id', person.id).select(PERSON_COLUMNS);
  fail(r.error, 'Not saved');
  const saved = (r.data as Person[] | null)?.[0];
  if (!saved) throw new Error('Not saved: you may not change the staff register.');
  return saved;
}

/**
 * Apply a TJR staff import (MB's R78; `planStaffImport`, `staffImportChanges` in @teslyn/domain): the new people in
 * one insert (names and roles only, active), then the roles of each matched person. The register's unique name
 * index refuses a name added meanwhile, so nobody is added twice. Returns the people added and given roles.
 */
export async function importStaff(
  sb: SupabaseClient,
  tenantId: string,
  changes: readonly StaffImportChange[],
): Promise<{ readonly added: number; readonly rolesGiven: number }> {
  const adds = changes.filter((c) => c.kind === 'add');
  if (adds.length > 0) {
    const r = await sb
      .from('person')
      .insert(
        adds.map((c) => ({
          tenant_id: tenantId,
          name: c.name,
          roles: c.roles,
          role: legacyPersonRole(c.roles),
          rank: null,
          title: '',
          active: c.active,
          // decision 137: TJR's crew phone numbers, read by every staff role
          phone: c.phone,
        })),
      )
      .select('id');
    fail(r.error, 'Not imported');
  }
  let rolesGiven = 0;
  for (const c of changes) {
    if (c.kind !== 'add_roles') continue;
    const r = await sb
      .from('person')
      .update({ roles: c.roles, role: legacyPersonRole(c.roles), ...(c.phone ? { phone: c.phone } : {}) })
      .eq('id', c.personId)
      .select('id');
    fail(r.error, `${c.name} was not given the roles from the list`);
    rolesGiven += (r.data ?? []).length;
  }
  return { added: adds.length, rolesGiven };
}

type SignInUser = { email: string; display_name: string; system?: boolean };

/** The organisation's sign-ins (one per person, whatever their roles), for "Teslyn sign-in". */
export async function loadSignIns(sb: SupabaseClient, tenantId: string): Promise<SignInChoice[]> {
  const r = await sb
    .from('membership')
    .select('user_id, app_user:user_id (email, display_name, system)')
    .eq('tenant_id', tenantId);
  fail(r.error, 'The sign-ins could not be read');
  const out = new Map<string, SignInChoice>();
  for (const row of (r.data ?? []) as unknown as {
    user_id: string;
    app_user: SignInUser | SignInUser[] | null;
  }[]) {
    const u = Array.isArray(row.app_user) ? row.app_user[0] : row.app_user;
    // a system account is never a register person's sign-in (decision 540; the database hides it from tenants)
    if (u?.system) continue;
    const label = u ? (u.display_name ? `${u.display_name} (${u.email})` : u.email) : row.user_id;
    out.set(row.user_id, { userId: row.user_id, label });
  }
  return [...out.values()].sort((a, b) => a.label.localeCompare(b.label, 'en-AU'));
}

/** The live crew rows of one project. */
export async function loadProjectCrew(sb: SupabaseClient, projectId: string): Promise<ProjectCrew[]> {
  const r = await sb
    .from('project_crew')
    .select(CREW_COLUMNS)
    .eq('project_id', projectId)
    .is('deleted_at', null)
    .order('created_at');
  fail(r.error, 'The project crew could not be read');
  return (r.data ?? []) as ProjectCrew[];
}

/** Add (`isNew`) or change a crew row; a row taken off the project by mistake gets `deleted_at`. */
export async function saveProjectCrew(
  sb: SupabaseClient,
  row: ProjectCrew,
  isNew: boolean,
): Promise<ProjectCrew> {
  const values = {
    tenant_id: row.tenant_id,
    project_id: row.project_id,
    person_id: row.person_id,
    role: row.role,
    start_date: row.start_date,
    end_date: row.end_date,
    note: row.note,
    deleted_at: row.deleted_at,
  };
  const r = isNew
    ? await sb.from('project_crew').insert(values).select(CREW_COLUMNS)
    : await sb.from('project_crew').update(values).eq('id', row.id).select(CREW_COLUMNS);
  fail(r.error, 'Not saved');
  const saved = (r.data as ProjectCrew[] | null)?.[0];
  if (!saved) throw new Error('Not saved: you may not change the project crew.');
  return saved;
}

/** A crew row with the person's name and title, as `project_crew_named` returns it. */
export interface NamedCrewRow extends NamedCrew {
  readonly id: string;
  readonly project_id: string;
  readonly person_id: string;
  readonly role: RegisterRole;
  readonly title: string;
  readonly person_active: boolean;
}

/**
 * The crew of several projects with names, for the Operations Hub cards: pass the projects' ids and turn each
 * project's rows into card lines with `crewCardLines(rows, today)` (@teslyn/domain).
 */
export async function loadCrewNamed(
  sb: SupabaseClient,
  projectIds: readonly string[],
): Promise<Map<string, NamedCrewRow[]>> {
  const out = new Map<string, NamedCrewRow[]>();
  if (projectIds.length === 0) return out;
  const r = await sb
    .from('project_crew_named')
    .select('id, project_id, person_id, role, start_date, end_date, name, title, person_active')
    .in('project_id', [...projectIds]);
  fail(r.error, 'The project crew could not be read');
  for (const row of (r.data ?? []) as NamedCrewRow[]) {
    out.set(row.project_id, [...(out.get(row.project_id) ?? []), row]);
  }
  return out;
}

/**
 * The Operations Hub's crew loader (`HubCrewLoader`, decision 422): for each project, the crew in force on `today()`
 * in register order then by name, with the role in words. Every project asked for gets an entry, empty when no one
 * is on it today.
 */
export function hubCrewLoader(sb: SupabaseClient, today: () => string): HubCrewLoader {
  return async (projectIds) => {
    const named = await loadCrewNamed(sb, projectIds);
    const date = today();
    const out = new Map<string, HubCrewMember[]>();
    for (const id of projectIds) {
      const seen = new Set<string>();
      const members = crewOnDate(named.get(id) ?? [], date)
        .sort(
          (a, b) =>
            REGISTER_ROLES.indexOf(a.role) - REGISTER_ROLES.indexOf(b.role) ||
            a.name.localeCompare(b.name, 'en-AU'),
        )
        .filter((r) => !seen.has(`${r.person_id}:${r.role}`) && seen.add(`${r.person_id}:${r.role}`))
        .map((r) => ({ name: r.name, role: REGISTER_ROLE_LABEL[r.role] }));
      out.set(id, members);
    }
    return out;
  };
}
